import { observable, action, computed } from "mobx";

const defaultPriorityUpperBound = 3;

const IS_BIRD_APP = process.env.REACT_APP_ENV === "bird";

export default class CreateUpdateEntityStore {
  constructor(http) {
    this.http = http;
    this.fetchSuggestionFields();
  }

  @observable type = null;
  @observable visible = false;
  @observable creationFields = [];
  @observable typesForFields = {};
  @observable labelsForFields = {};
  @observable fieldsForLabels = {};
  @observable lovByFieldName = {};
  
  @observable
  requestMessage = {
    actionType: "CreateEntity",
    entity: {},
    targetBid: null
  };

  @observable searchValueByField = observable.map();
  @observable searchValueByTidAndField = observable.map();

  @observable suggestionFields = {};
  
  @observable suggestionsByField = observable.map();
  @observable suggestionsByTidAndField = observable.map();

  @observable lastModifiedIdField = null;
  @observable lastModifiedTidAndField = {
    tid: null,
    field: null
  };

  @observable updateModalVisible = false;
  @observable updateModalData = {
    tid: null,
    cellInfo: null,
    field: null,
    fieldType: null,
    label: null,
    results: null,
    updateEntity: () => {}
  }

  @observable selectedLovInUpdateModal = [];

  @action
  setUpdateModalData = (data) => {
    this.updateModalData = data;
  }

  @action
  toggleUpdateModalVisibility = () => {
    this.searchValueByTidAndField.clear();
    this.selectedLovInUpdateModal = [];
    this.updateModalVisible = !this.updateModalVisible;
  }

  @action
  setLastModifiedIdField = (field) => {
    this.lastModifiedIdField = field;
  }

  @action
  setLastModifiedTidAndField = (tid, field) => {
    this.lastModifiedTidAndField = {
      tid: tid,
      field: field
    }
  }

  @action
  changeSearchValue = (event, { newValue }) => {
    this.searchValueByField.set(this.lastModifiedIdField, newValue);
    this.updateRequestMessage(this.lastModifiedIdField, newValue);
  }

  @action
  changeSearchValueForTidAndField = (event, { newValue }) => {
    this.searchValueByTidAndField.set(this.lastModifiedTidAndField.tid, { [this.lastModifiedTidAndField.field]: newValue });
  }

  @action
  fetchSuggestionFields = () => {
    if(IS_BIRD_APP) return;
    
    this.http.fetchSuggestionFields().then((fields) => {
      this.suggestionFields = fields;
    });
  }

  @action
  fetchSuggestions = (branchId, field) => {
    if(IS_BIRD_APP) return;
    
    const searchValue = this.searchValueByField.get(field);
    if(searchValue !== undefined && searchValue.length >= 3) {
      this.http.fetchSuggestions(branchId, field, searchValue, defaultPriorityUpperBound).then((suggestions) => {
        this.suggestionsByField.set(field, suggestions);
      })
    }
  }

  @action
  fetchUpdateSuggestions = (branchId, tid, field) => {
    if(IS_BIRD_APP) return;
    
    const searchValuesForTidByField = this.searchValueByTidAndField.get(tid);
    if(searchValuesForTidByField !== undefined) {
      const searchValue = searchValuesForTidByField[field];
      if(searchValue !== undefined && searchValue.length >= 3) {
        this.http.fetchSuggestions(branchId, field, searchValue, defaultPriorityUpperBound).then((suggestions) => {
          this.suggestionsByTidAndField.set(tid, {[field]: suggestions});
        })
      }
    }
  }

  @action
  clearSuggestions = (field) => {
    this.suggestionsByField.set(field, []);
  }

  @action
  clearUpdateSuggestions = (tid) => {
    this.suggestionsByTidAndField.set(tid, {});
  }

  @action
  toggleModalVisibility = () => {
    this.resetRequestMessage();
    this.lastModifiedIdField = null;
    this.searchValueByField.clear();
    this.suggestionsByField.clear();
    this.selectedLovInUpdateModal = [];
    this.visible = !this.visible;
  };

  @action
  resetRequestMessage = () => {
    this.requestMessage = {
      actionType: "CreateEntity",
      entity: {},
      targetBid: null
    }
  }

  @action
  setType = type => {
    this.type = type;
    this.requestMessage.entity.type = type;
  };

  @action
  setFieldsData = (creationFields, typesForFields, labelsForFields, fieldsForLabels, lovByFieldName) => {
    this.creationFields = creationFields;
    this.typesForFields = typesForFields;
    this.labelsForFields = labelsForFields;
    this.fieldsForLabels = fieldsForLabels;
    this.lovByFieldName = lovByFieldName;
  };

  @action
  setBranchId = (bid) => {
    this.requestMessage = {
        ...this.requestMessage,
        targetBid: bid
    }
  }

  @action
  updateRequestMessage = (field, value) => {
    this.requestMessage.entity[field] = value;
  }

  @action
  handleChangesForDynamicFields = (entityType, fromField, valueToCheck) => {
    if(entityType === "Variable"){
      switch (fromField) {
        case this.fieldsForLabels.MAINTENANCE_AGENCY_ID:
          const fieldValue = valueToCheck?.toLowerCase() === "ecb" ? true : undefined;
          this.requestMessage.entity[this.fieldsForLabels.IS_NORMALISED] = fieldValue;
          break;
        default:
          break;
      }
    }
  }

  sendCreateEntityRequest = () => {
    if(IS_BIRD_APP) return;
    
    this.http.func_sendGeneralMessage(this.requestMessage);
  }

  getRequestMessageFieldValueByField = (field) => {
    return computed(() => {
      return this.requestMessage.entity[field];
    }).get();
  }

  @computed
  get getType() {
    return this.type;
  }

  @computed
  get isModalVisible() {
    return this.visible;
  }

  @computed
  get getCreationFields() {
    return this.creationFields;
  }

  @computed
  get getLabelsForFields() {
    return this.labelsForFields;
  }

  getSearchValueForField = (field) => {
    return computed(() => {
      const searchValue = this.searchValueByField.get(field);
      if(searchValue !== undefined) {
        return searchValue;
      } else {
        return "";
      }
    }).get();
  }

  getSearchValueForTidAndField = (tid, field) => {
    return computed(() => {
      const searchValuesForTidByField = this.searchValueByTidAndField.get(tid);
      if(searchValuesForTidByField !== undefined) {
        const searchValue = searchValuesForTidByField[field];
        return searchValue;
      } else {
        return undefined;
      }
    }).get();
  }

  @computed
  get getSuggestionFields() {
    return this.suggestionFields;
  }

  getSuggestionsForField = (field) => {
    return computed(() => {
      const suggestions = this.suggestionsByField.get(field)
      if(suggestions !== undefined) {
        return suggestions;
      } else {
        return [];
      }
    }).get();
  }

  getSuggestionsForTidAndField = (tid, field) => {
    return computed(() => {
      const suggestionsForTidByField = this.suggestionsByTidAndField.get(tid)
      if(suggestionsForTidByField !== undefined) {
        const suggestions = suggestionsForTidByField[field];
        if(suggestions !== undefined) {
          return suggestions;
        } else {
          return [];
        }
      } else {
        return [];
      }
    }).get();
  }

  getTypeForField = field => {
    return computed(() => this.typesForFields[field]).get();
  };

  getNotValidLongFields = () => {
    const longFieldsWithNotValidValue = [];
    this.creationFields.forEach(creationField => {
      if(this.typesForFields[creationField] === "Long" && isNaN(this.requestMessage.entity[creationField])) {
        longFieldsWithNotValidValue.push(this.labelsForFields[creationField]);
      }
    });
    return longFieldsWithNotValidValue;
  }

  @computed
  get isUpdateModalVisible() {
    return this.updateModalVisible;
  }

  @computed
  get getUpdateModalData() {
    return this.updateModalData;
  }

  getLovForFieldName = fieldName => {
    const lov = this.lovByFieldName[fieldName];
    return lov && lov.length ? lov : []; 
  }

  @action
  onChangeSelectedLov = selectedLov => {
    if (!selectedLov) {
      this.selectedLovInUpdateModal = []
    } else {
      this.selectedLovInUpdateModal = Array.isArray(selectedLov) ? selectedLov : [selectedLov]
    }
  }

  @computed
  get getSelectedLovInUpdateModal() {
    return this.selectedLovInUpdateModal
  }
}
